// based on https://github.com/mikelpmc/react-multiline-clamping/blob/master/src/lib/clamp.js
import { formatNumber } from "@audacia-hq/shared/utils";
import { Menu } from "@headlessui/react";
import { IconChevronDown, IconCircleX, IconWorld } from "@tabler/icons-react";
import clsx from "clsx";
import { useField } from "formik";
import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber";
import countries from "i18n-iso-countries";
import countryDE from "i18n-iso-countries/langs/de.json";
import countryEN from "i18n-iso-countries/langs/en.json";
import countryFR from "i18n-iso-countries/langs/fr.json";
import * as React from "react";
import { useEffect, useRef, useState } from "react";

import allCountries, { PhoneCountry } from "./Countries";
import Flag from "./Flag";

type PhoneType = "FIXED_LINE" | "MOBILE" | "FIXED_LINE_OR_MOBILE" | "TOLL_FREE" | "PREMIUM_RATE"
  | "SHARED_COST" | "VOIP" | "PERSONAL_NUMBER" | "PAGER" | "UAN" | "VOICEMAIL" | "UNKNOWN"

export interface PhoneInputProps {
  id?: string;
  name: string;
  label?: string;
  labelIcon?: React.ReactNode;
  className?: string;
  required?: boolean
  excludeCountries?: string[];
  includedCountries?: string[];
  selectedCountry?: string;
  acceptedPhoneTypes?: PhoneType[];
  phoneTypeErrorLabel?: string;
  placeholderType?: PhoneType;
  disabled?: boolean;
  language?: string;
}

export const numberType = {
  FIXED_LINE: 0,
  MOBILE: 1,
  FIXED_LINE_OR_MOBILE: 2,
  TOLL_FREE: 3,
  PREMIUM_RATE: 4,
  SHARED_COST: 5,
  VOIP: 6,
  PERSONAL_NUMBER: 7,
  PAGER: 8,
  UAN: 9,
  VOICEMAIL: 10,
  UNKNOWN: -1,
};

const defaultExcludeList = ["AC",
  "AL", "BZ", "MZ", "JM", "SM", "VG", "AF",
  "ET", "CV", "SW", "SZ", "ER", "KY", "MH",
  "FJ", "MM", "MD", "GH", "AI", "AG", "DM",
  "AS", "MK", "UG", "TZ", "MW", "ME", "PW",
  "GL", "LC", "BY", "SS", "LS", "GW", "LR",
  "PM", "MS", "ZM", "KP", "SO", "SL", "FM",
  "TL", "MR", "GM", "MG", "CD", "MV", "CU",
  "CF", "TN", "PG", "SB", "SC", "VU", "AS",
  "TO", "CK", "KI", "IO", "FK", "NF"];

const PhoneInput = ({
  id, name, label, className, excludeCountries = defaultExcludeList, includedCountries, selectedCountry,
  acceptedPhoneTypes, phoneTypeErrorLabel, placeholderType, disabled, required, language = "fr", labelIcon,
}: PhoneInputProps) => {
  switch (language) {
    case "fr": countries.registerLocale(countryFR); break;
    case "de": countries.registerLocale(countryDE); break;
    default: countries.registerLocale(countryEN);
  }
  const displayedCountries = allCountries.filter((c) => (includedCountries
    ? includedCountries.includes(c.iso2)
    : !(excludeCountries || []).includes(c.iso2)))
    .map((c) => ({ ...c, name: countries.getName(c.iso2, language) }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const [placeholder, setPlaceholder] = useState<string>("");
  const inputField = useRef<HTMLInputElement | null>(null);

  const [_field, meta, helpers] = useField(name);
  const [_, countryMeta, countryHelpers] = useField(`${name}Country`);
  const { value, error, touched } = meta;
  const { setValue, setError, setTouched } = helpers;
  const { value: formCountryValue } = countryMeta;
  const { setValue: setFormCountryValue } = countryHelpers;

  const mapCountry = (phoneNumber: string) => {
    if (!phoneNumber || !phoneNumber.startsWith("+") || phoneNumber.length < 2) return null;
    return displayedCountries.find((c) => phoneNumber.startsWith(`+${c.dialCode}`) && c.priority === 0);
  };

  const preselectedCountry = formCountryValue || selectedCountry;
  const [country, setCountry] = useState<PhoneCountry | null>(
    displayedCountries.find((c) => c.iso2 === preselectedCountry)
    || (value && mapCountry(value))
    || null,
  );

  useEffect(() => {
    const preselectedCountry = formCountryValue || selectedCountry;
    setCountry((prev) => prev
      || (value && mapCountry(value))
      || displayedCountries.find((c) => c.iso2 === preselectedCountry));
  }, [selectedCountry, formCountryValue]);

  useEffect(() => {
    setPlaceholder(country
      ? getPhonePlaceholder(country.iso2, placeholderType ? numberType[placeholderType] : numberType.MOBILE, true)
      : "");
  }, [country, placeholderType]);

  const onSelfChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e?.currentTarget?.value;
    setTouched(true);
    setError(undefined);
    findCountry(val);
    setValue(country ? formatNumber(val, "INTERNATIONAL", country.iso2) : val);
  };

  const findCountry = (val: string) => {
    if (!country || !val.startsWith(`+${country.dialCode}`)) {
      const countryMatch = mapCountry(val);
      if (countryMatch) selectCountry(countryMatch);
    }
  };

  const selectCountry = (newCountry: PhoneCountry | null) => {
    if (inputField?.current?.value?.length && country && newCountry) {
      inputField.current.value = inputField.current.value.replace(`+${country.dialCode}`, `+${newCountry.dialCode}`);
    }
    setCountry(newCountry);
    setFormCountryValue(newCountry?.iso2);
  };

  const onCountrySelect = (newCountry: PhoneCountry | null) => {
    selectCountry(newCountry);
  };

  const getPhonePlaceholder = (iso2: string, type: number = numberType.MOBILE, nationalFormat = true) => {
    try {
      const phoneUtil = PhoneNumberUtil.getInstance();
      const numberObj = phoneUtil.getExampleNumberForType(iso2, type);
      return phoneUtil.format(numberObj, nationalFormat
        ? PhoneNumberFormat.NATIONAL
        : PhoneNumberFormat.INTERNATIONAL);
    } catch (err) {
      return "";
    }
  };

  const getPhoneNumberType = (number: string, iso2: string) => {
    try {
      const phoneUtil = PhoneNumberUtil.getInstance();
      const numberObj = phoneUtil.parseAndKeepRawInput(number, iso2);
      return phoneUtil.getNumberType(numberObj);
    } catch (err) {
      return -1;
    }
  };

  const validatePhoneType = () => {
    if (value && country) {
      const inputType = getPhoneNumberType(value, country.iso2);
      const acceptedNumberTypes = (acceptedPhoneTypes || []).map((type) => (typeof numberType[type] !== "undefined" ? numberType[type] : -1));
      if (typeof acceptedNumberTypes.find((type) => type === inputType) === "undefined") {
        setError(phoneTypeErrorLabel || "invalidPhoneType");
      }
    }
  };

  const inputBaseStyle = `
    focus:outline-hidden w-full border border-border-strong rounded-lg
    disabled:bg-bg-disabled disabled:text-text-disabled disabled:border-border-disabled
    focus:outline-focus focus:outline-offset-1
    active:border-border-interactive-base active:outline-input-interactive active:outline-offset-0
  `;
  const inputErrorStyle = "border-border-danger-base! outline-input-danger active:border-border-danger-base! active:outline-input-danger";
  const inputDisabledStyle = "bg-bg-disabled text-text-disabled border-border-disabled outline-0 outline-offset-0 active:border-border-disabled";
  const buttonStyle = `
    flex p-2.5 gap-1 bg-button-transparent-base text-icon-bg-subtle items-center rounded-l-lg cursor-pointer border-r border-border-strong
    hover:bg-button-transparent-hover active:bg-button-transparent-pressed
    disabled:bg-button-transparent-disabled disabled:text-icon-disabled disabled:cursor-not-allowed disabled:border-border-disabled
  `;

  return (
    <div className="w-[300px] relative flex flex-col typography-medium-base gap-1 text-text-base">
      <label className="flex gap-1" htmlFor={name}>
        <span>{label}</span>
        {required && <span className="text-text-danger">*</span>}
        {labelIcon && <span className="text-icon-bg-subtle">{labelIcon}</span>}
      </label>

      <div
        className={clsx(
          inputBaseStyle,
          error && inputErrorStyle,
          disabled && inputDisabledStyle,
          className,
        )}
      >
        <div className="relative flex items-stretch z-10">
          <Menu>
            {({ open }) => (
              <>
                <Menu.Button className={buttonStyle} disabled={disabled}>
                  {country
                    ? (
                      <Flag country={country.iso2} />
                    )
                    : (
                      <IconWorld />
                    )}
                  <IconChevronDown className={`transform transition duration-300 ease-in-out  ${open && "rotate-180"}`} />
                </Menu.Button>
                <Menu.Items className="h-52 shadow-lg overflow-y-scroll absolute top-full right-0 left-0 bg-bg-base rounded-lg">
                  {displayedCountries.map((c) => (
                    <Menu.Item key={c.iso2}>
                      {({ active }) => (
                        <div
                          className="flex gap-3 py-2 px-3 hover:bg-button-transparent-hover items-center cursor-pointer"
                          onClick={() => { onCountrySelect(c); }}
                          onKeyDown={() => { onCountrySelect(c); }}
                        >
                          <Flag country={c.iso2} />
                          <div>{c.name}</div>
                          <div className="text-text-subtle">{`+${c.dialCode}`}</div>
                        </div>
                      )}
                    </Menu.Item>
                  ))}
                </Menu.Items>
              </>
            )}
          </Menu>

          <input
            id={id}
            className="grow focus:outline-hidden rounded-r-lg py-2 px-3 "
            placeholder={placeholder}
            onChange={(e) => { onSelfChange(e); }}
            onBlur={validatePhoneType}
            ref={inputField}
            value={value}
            disabled={disabled}
          />
        </div>
      </div>
      {(touched && error) && (
        <div className="flex gap-1">
          <IconCircleX className="text-icon-danger" />
          <span className="text-text-danger">{error}</span>
        </div>
      )}
    </div>
  );
};

export default PhoneInput;
